import { useEffect, useMemo } from 'react';
import { MapContainer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LockSimple } from '@phosphor-icons/react';
import VectorTiles from './VectorTiles';
import { MAP_ROUTE } from '../constants/colors';

// divIcon собирается из строки HTML — var(--…) там не резолвится,
// поэтому литералы, как и на странице прогулки
const stopIcon = (n) => L.divIcon({
  className: '',
  html: `<div style="width:24px;height:24px;border-radius:50%;background:${MAP_ROUTE};border:2px solid #fff;display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;font-size:12px;">${n}</div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

/** Вписывает все точки в рамку превью с запасом по краям под маркеры */
const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    // Карта монтируется до того, как вёрстка страницы устоялась, и Leaflet
    // считает рамку по устаревшему размеру — точки сжимались в центр.
    // Пересчитываем размер перед вписыванием.
    //
    // Без анимации: превью неподвижное, а панель редактора маршрута
    // закрывалась посреди анимации масштаба — Leaflet дорабатывал её
    // на уже удалённой карте и падал (`_leaflet_pos` of undefined)
    const fit = () => {
      map.invalidateSize();
      if (positions.length > 1) map.fitBounds(positions, { padding: [24, 24], maxZoom: 16, animate: false });
      else if (positions.length === 1) map.setView(positions[0], 15, { animate: false });
    };
    fit();
    const timer = setTimeout(fit, 250);
    return () => clearTimeout(timer);
  }, [map, positions]);
  return null;
};

/** Приставку «Leaflet» убираем, как на странице прогулки, — ODbL её не требует */
const AttributionPrefix = () => {
  const map = useMap();
  useEffect(() => { map.attributionControl?.setPrefix(false); }, [map]);
  return null;
};

/**
 * Превью маршрута на витрине: линия и номера точек, без взаимодействия.
 *
 * Карта неподвижна намеренно: она лежит в прокручиваемой странице, и любой
 * жест внутри неё перехватывал бы скролл. Смотреть маршрут подробно —
 * на странице прогулки.
 *
 * `blurred` — для платного маршрута до покупки: виден район, но не путь.
 * Размывается только слой карты (`.leaflet-map-pane`), атрибуция остаётся
 * читаемой — ODbL требует указания авторства.
 */
const RouteMapPreview = ({ stops, blurred = false, path = null }) => {
  const positions = useMemo(
    () => stops
      .filter(s => s.place?.latitude != null && s.place?.longitude != null)
      .map(s => [s.place.latitude, s.place.longitude]),
    [stops]
  );

  if (positions.length === 0) return null;

  return (
    <div
      // `z-0` — свой контекст наложения: слои Leaflet идут с z-index 400+
      // и без него проезжали бы поверх липкой панели и нижнего меню
      className={`relative z-0 h-[180px] overflow-hidden rounded-lg border border-theme-border ${blurred ? 'route-map-blurred' : ''}`}
      // Без aria-hidden превью читалось бы скринридером как набор чисел
      aria-hidden="true"
    >
      <MapContainer
        center={positions[0]}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
        // Дробный масштаб: с целым шагом вытянутый маршрут вписывался
        // на уровень мельче и занимал треть превью
        zoomSnap={0.25}
        dragging={false}
        touchZoom={false}
        doubleClickZoom={false}
        scrollWheelZoom={false}
        boxZoom={false}
        keyboard={false}
      >
        <AttributionPrefix />
        <VectorTiles />
        <FitBounds positions={positions} />
        {/* `path` — пеший путь, если построен (редактор своего маршрута);
            без него — места по прямой точками, как «впереди» на прогулке */}
        {/* Разные key: react-leaflet не обновляет стиль у уже созданной
            линии, и без них пунктир оставался и после прихода пути */}
        {path && path.length > 1
          ? <Polyline key="path" positions={path} pathOptions={{ color: MAP_ROUTE, weight: 4, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }} />
          : <Polyline key="straight" positions={positions} color={MAP_ROUTE} weight={3} opacity={0.8} dashArray="8, 6" />}
        {!blurred && positions.map((p, i) => (
          <Marker key={i} position={p} icon={stopIcon(i + 1)} interactive={false} />
        ))}
      </MapContainer>

      {blurred && (
        <div className="pointer-events-none absolute inset-0 z-[500] flex items-center justify-center">
          <div className="flex items-center gap-2 rounded-full border border-theme-border bg-theme-card px-4 py-2 text-body-sm text-theme-text-primary">
            <LockSimple size={16} weight="bold" />
            Маршрут откроется после покупки
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteMapPreview;
